import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Eye, EyeOff, Download } from 'lucide-react';
import LegalDocumentModal from '../components/LegalDocumentModal';
import { LEGAL_DOCUMENTS } from '../content/legalTexts';
import { PlatformIconBadge } from '../components/OsPlatformIcons';
import DesktopSyncBar from '../components/DesktopSyncBar';

const DEFAULT_WINDOWS_DOWNLOAD = {
  fileName: 'ROYSIS_Local_Setup_v28.exe',
  downloadUrl: '/downloads/ROYSIS_Local_Setup_v28.exe',
};

const PLATFORM_DOWNLOADS = [
  {
    id: 'windows',
    title: 'Windows Kurulum Dosyası',
    fileName: DEFAULT_WINDOWS_DOWNLOAD.fileName,
    buttonLabel: 'Setup Paketini İndir',
    downloadUrl: DEFAULT_WINDOWS_DOWNLOAD.downloadUrl,
    borderClass: 'border-blue-400/30',
    bgClass: 'bg-blue-500/10',
    iconBgClass: 'bg-white/95 shadow-lg shadow-blue-900/25',
    iconSrc: '/icons/windows-logo.png',
    iconAlt: 'Windows logosu',
    textClass: 'text-blue-100',
    buttonClass: 'bg-blue-600 hover:bg-blue-500',
  },
  {
    id: 'ios',
    title: 'iOS Kurulum Dosyası',
    fileName: 'ROYSIS_iOS.mobileconfig',
    buttonLabel: 'iOS Profilini İndir',
    downloadUrl: '/downloads/ROYSIS_iOS.mobileconfig',
    borderClass: 'border-slate-400/30',
    bgClass: 'bg-slate-500/10',
    iconBgClass: 'bg-gradient-to-br from-[#2b2b2f] via-[#1d1d1f] to-black shadow-lg shadow-black/40',
    iconSrc: '/icons/apple-logo.png',
    iconAlt: 'Apple logosu',
    iconBadgeClass: 'h-14 w-14',
    iconSizeClass: 'h-11 w-11',
    textClass: 'text-slate-200',
    buttonClass: 'bg-slate-600 hover:bg-slate-500',
  },
  {
    id: 'linux',
    title: 'Linux Kurulum Dosyası',
    fileName: 'ROYSIS_Linux_x64.tar.gz',
    buttonLabel: 'Linux Paketini İndir',
    downloadUrl: '/downloads/ROYSIS_Linux_x64.tar.gz',
    borderClass: 'border-emerald-400/30',
    bgClass: 'bg-emerald-500/10',
    iconBgClass: 'bg-white/95 shadow-lg shadow-emerald-900/20',
    iconSrc: '/icons/linux-tux.png',
    iconAlt: 'Linux logosu',
    textClass: 'text-emerald-100',
    buttonClass: 'bg-emerald-600 hover:bg-emerald-500',
  },
];

const Login = () => {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeLegalDocument, setActiveLegalDocument] = useState(null);
  const [isOfflineLocalApp, setIsOfflineLocalApp] = useState(false);
  const [platformDownloads, setPlatformDownloads] = useState(PLATFORM_DOWNLOADS);

  useEffect(() => {
    fetch(`/downloads/local-version.json?t=${Date.now()}`, { cache: 'no-store' })
      .then((response) => (response.ok ? response.json() : null))
      .then((manifest) => {
        if (!manifest?.downloadUrl) {
          return;
        }

        const fileName = manifest.downloadUrl.split('/').pop();
        if (!fileName) {
          return;
        }

        setPlatformDownloads((current) => current.map((platform) => (
          platform.id === 'windows'
            ? {
              ...platform,
              fileName,
              downloadUrl: `/downloads/${fileName}`,
              versionLabel: manifest.version ? `v${manifest.version}` : '',
            }
            : platform
        )));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (window.ROYSIS_DESKTOP?.mode === 'cloud') {
      setIsOfflineLocalApp(false);
      return;
    }

    fetch('/api/health')
      .then((response) => response.json())
      .then((data) => {
        setIsOfflineLocalApp(data?.mode === 'local-installer');
      })
      .catch(() => {
        setIsOfflineLocalApp(false);
      });
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      await login(data);
      toast.success('Giriş başarılı!');
      navigate('/');
    } catch (error) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error('Giriş başarısız');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {
    setIsLoading(true);
    try {
      await login({
        companyCode: 'demo',
        username: 'admin',
        password: 'admin123'
      });
      toast.success('Demo moduna hoş geldiniz!');
      navigate('/');
    } catch (error) {
      toast.error('Demo sistemine bağlanılamadı. Lütfen daha sonra tekrar deneyin.');
    } finally {
      setIsLoading(false);
    }
  };

  const clearBrowserCache = () => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    }
    caches.keys().then((names) => {
      for (let name of names) caches.delete(name);
    });
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload(true);
  };

  return (
    <div className="min-h-screen bg-slate-100 font-sans">
      <div className="fixed top-0 left-0 z-50 w-full bg-orange-600 px-3 py-1.5 text-center text-xs font-bold text-white shadow-md sm:text-sm">
        ROYSİS YENİ SÜRÜM AKTİF - ÜCRETSİZ DEMO EKLEDİ (5 İŞLEM)
      </div>
      <div className="fixed left-0 top-8 z-40 w-full">
        <DesktopSyncBar />
      </div>

      <div className="mx-auto flex min-h-screen max-w-6xl items-center px-4 pb-6 pt-24 sm:px-6 lg:px-8">
        <div className="grid w-full gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="flex flex-col rounded-[28px] bg-slate-900 p-6 text-white shadow-xl sm:p-8">
            <div className="flex items-center gap-4">
              <img
                src="/favicon.svg"
                alt="ROYSIS logosu"
                className="h-16 w-16 rounded-2xl shadow-lg shadow-blue-900/30"
              />
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight sm:text-4xl">ROYSIS</h1>
                <p className="mt-2 inline-flex items-center rounded-full border border-emerald-400/40 bg-emerald-500/15 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-200">
                  produced by penguen
                </p>
                <p className="mt-1 text-sm font-medium text-slate-300">
                  Hesabınıza giriş yapın
                  <span className="ml-2 rounded bg-blue-500/20 px-2 py-0.5 text-[10px] font-bold text-blue-200">
                    v2.2 (GÜNCEL)
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-6 grid gap-3 sm:grid-cols-3">
              {platformDownloads.map((platform) => (
                  <div
                    key={platform.id}
                    className={`min-w-0 overflow-hidden rounded-2xl border p-4 ${platform.borderClass} ${platform.bgClass}`}
                  >
                    <div className="flex min-w-0 flex-col items-start gap-3">
                      <PlatformIconBadge platform={platform} />
                      <div className="w-full min-w-0">
                        <p className="text-sm font-extrabold leading-snug text-white">{platform.title}</p>
                        <p className={`mt-1 break-all text-[11px] leading-5 ${platform.textClass}`}>
                          {platform.fileName}
                        </p>
                        {platform.versionLabel && (
                          <span className="mt-1 inline-block rounded bg-white/15 px-1.5 py-0.5 text-[10px] font-bold">
                            {platform.versionLabel}
                          </span>
                        )}
                        {platform.downloadUrl ? (
                          <div className="mt-3 space-y-2">
                            <a
                              href={platform.downloadUrl}
                              download={platform.fileName}
                              className={`inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white transition ${platform.buttonClass}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                              {platform.buttonLabel}
                            </a>
                            {platform.altDownloadUrl && (
                              <a
                                href={platform.altDownloadUrl}
                                download={platform.altFileName}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-2 text-xs font-bold text-white transition hover:bg-white/20"
                              >
                                <Download className="h-3.5 w-3.5" />
                                {platform.altButtonLabel}
                              </a>
                            )}
                            {platform.id === 'ios' && (
                              <p className={`break-words text-[10px] leading-4 ${platform.textClass} opacity-90`}>
                                iPhone/iPad: profili indirip Ayarlar &gt; Genel &gt; VPN ve Aygıt Yönetimi üzerinden kurun.
                              </p>
                            )}
                          </div>
                        ) : (
                          <button
                            type="button"
                            disabled
                            className={`mt-3 inline-flex w-full cursor-not-allowed items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-bold text-white/80 ${platform.buttonClass}`}
                            aria-disabled="true"
                          >
                            <Download className="h-3.5 w-3.5" />
                            {platform.buttonLabel}
                          </button>
                        )}
                        {!platform.downloadUrl && (
                          <p className={`mt-2 text-[10px] font-semibold leading-4 ${platform.textClass} opacity-80`}>
                            İndirme yakında aktif olacaktır.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-orange-400/30 bg-orange-500/10 p-4">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-200">Demo Erişimi</p>
              <p className="mt-2 text-sm leading-6 text-slate-200">
                Sistemi hızlı test etmek için demo hesabı tek tıkla açılır.
              </p>
              <p className="mt-3 rounded-lg border border-amber-400/40 bg-amber-500/15 px-3 py-2 text-[11px] leading-5 text-amber-100">
                Demo hesaptaki veriler herkese açıktır, lütfen kişisel veya hassas veri girişi yapmayınız.
              </p>
              <button
                type="button"
                onClick={handleDemoLogin}
                disabled={isLoading}
                className="mt-4 flex w-full justify-center rounded-xl border-2 border-orange-400 bg-orange-500 px-4 py-3 text-sm font-black text-white transition hover:bg-orange-400 disabled:cursor-not-allowed disabled:opacity-70"
              >
                ÜCRETSİZ DEMO GİRİŞİ
              </button>
            </div>
          </div>

          <div className="rounded-[28px] bg-white p-6 shadow-xl sm:p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-extrabold tracking-tight text-gray-900">Hesabınıza giriş yapın</h2>
              <p className="mt-2 text-sm text-gray-600">
                Firma kodunuz ve kullanıcı bilgilerinizle sisteme bağlanın.
              </p>
            </div>

            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              {isOfflineLocalApp && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-900">
                  <strong>Masaüstü (offline) kurulum.</strong> penguen.info üzerinden değiştirdiğiniz şifre buraya yansımaz.
                  Şifre sorunu için Başlat menüsünden <strong>ROYSIS Admin Şifresi Sıfırla</strong> çalıştırın
                  veya <strong>ÜCRETSİZ DEMO GİRİŞİ</strong> kullanın.
                </div>
              )}

              <div>
                <label htmlFor="companyCode" className="block text-sm font-bold text-gray-900">
                  Firma Kodu
                </label>
                <input
                  {...register('companyCode', { required: 'Firma kodu gereklidir' })}
                  type="text"
                  className="mt-1 block w-full rounded-xl border-2 border-gray-900 bg-white px-4 py-3 text-black shadow-sm placeholder-gray-500 focus:border-blue-600 focus:ring-blue-600 sm:text-base"
                  placeholder="Örn: firma1"
                  style={{ color: 'black', fontWeight: '700', opacity: '1' }}
                />
                {errors.companyCode && (
                  <p className="mt-1 text-sm font-bold text-red-600">{errors.companyCode.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="username" className="block text-sm font-bold text-gray-900">
                  Kullanıcı Adı
                </label>
                <input
                  {...register('username', { required: 'Kullanıcı adı gereklidir' })}
                  type="text"
                  className="mt-1 block w-full rounded-xl border-2 border-gray-900 bg-white px-4 py-3 text-black shadow-sm placeholder-gray-500 focus:border-blue-600 focus:ring-blue-600 sm:text-base"
                  placeholder="Kullanıcı adınızı girin"
                  style={{ color: 'black', fontWeight: '700', opacity: '1' }}
                />
                {errors.username && (
                  <p className="mt-1 text-sm font-bold text-red-600">{errors.username.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-bold text-gray-900">
                  Şifre
                </label>
                <div className="relative mt-1">
                  <input
                    {...register('password', { required: 'Şifre gereklidir' })}
                    type={showPassword ? 'text' : 'password'}
                    className="block w-full rounded-xl border-2 border-gray-900 bg-white px-4 py-3 pr-12 text-black shadow-sm placeholder-gray-500 focus:border-blue-600 focus:ring-blue-600 sm:text-base"
                    placeholder="Şifrenizi girin"
                    style={{ color: 'black', fontWeight: '700', opacity: '1' }}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-600" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm font-bold text-red-600">{errors.password.message}</p>
                )}
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex w-full justify-center rounded-xl border border-transparent bg-blue-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isLoading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
                  ) : (
                    'Giriş Yap'
                  )}
                </button>
                <p className="mt-2 text-center text-[11px] leading-5 text-amber-700">
                  Demo hesaptaki veriler herkese açıktır, lütfen kişisel veya hassas veri girişi yapmayınız.
                </p>
              </div>
            </form>

            <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-center text-xs leading-5 text-gray-600">
              <p>
                Giriş yaparak veya demo hesabını kullanarak{' '}
                <button
                  type="button"
                  onClick={() => setActiveLegalDocument(LEGAL_DOCUMENTS.terms)}
                  className="font-bold text-blue-600 underline-offset-2 hover:underline"
                >
                  Kullanıcı Sözleşmesi
                </button>
                {' '}ve{' '}
                <button
                  type="button"
                  onClick={() => setActiveLegalDocument(LEGAL_DOCUMENTS.kvkk)}
                  className="font-bold text-blue-600 underline-offset-2 hover:underline"
                >
                  KVKK Aydınlatma Metni
                </button>
                &apos;ni okuduğunuzu ve kabul ettiğinizi beyan etmiş sayılırsınız.
              </p>
            </div>

            <div className="mt-6 border-t border-gray-100 pt-5 text-center">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                Produced by Penguen
              </p>
              <button
                onClick={clearBrowserCache}
                className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-400 transition hover:text-blue-600"
              >
                Sistemi Sıfırla / Önbelleği Temizle
              </button>
            </div>
          </div>
        </div>
      </div>

      <LegalDocumentModal
        legalDocument={activeLegalDocument}
        onClose={() => setActiveLegalDocument(null)}
      />
    </div>
  );
};

export default Login;
